/**
 * KSH Fan Meet — Auto-claim free ticket once purchase threshold is reached
 * ---------------------------------------------------------------------------
 * Handles THREE tiers, each mutually exclusive (customer gets the single
 * highest tier they qualify for), across TWO qualifying paths:
 *
 *   INTERNATIONAL customers qualify by GC (Gift Certificate) subtotal:
 *     SVIP        >= ₱12,000
 *     VIP         >= ₱8,000  and < ₱12,000
 *     Box Premium >= ₱6,000  and < ₱8,000
 *
 *   LOCAL (Philippines) customers qualify by REGULAR PRODUCT subtotal
 *   (SVIP is not available to Local customers per campaign mechanics):
 *     VIP         >= ₱8,000
 *     Box Premium >= ₱6,000  and < ₱8,000
 *
 * DESIGN (UPDATED): claiming now happens automatically as soon as a
 * customer qualifies — no click required. A fully-automatic version was
 * tried previously and caused duplicate lines / reload loops when state
 * didn't settle cleanly between overlapping eligibility checks. This
 * version re-introduces auto-claim but with an explicit in-flight lock
 * (`syncInProgress`) so a claim attempt can never overlap with another
 * eligibility check — that overlap was the actual root cause of the
 * earlier bug, not automation itself. Removing a ticket when the customer
 * no longer qualifies (or qualifies for a different tier) remains
 * automatic, as before.
 *
 * NO PAGE RELOAD IN THE PRIMARY PATH. The Sections API drawer swap
 * (updateCartDrawerDOM) shows the update instantly with zero navigation
 * whenever it succeeds. FALLBACK, if that fails (wrong/unconfirmed
 * cartDrawerSectionId, drawer not section-rendered in this theme):
 * reloadWithCooldown() does a real window.location.reload(), guarded by
 * an 8s cooldown so cart state flapping between two tiers can't trigger
 * rapid repeat reloads. This is a deliberate trade-off — the reload only
 * exists as a safety net for the one unconfirmed piece of config
 * (cartDrawerSectionId); once that's confirmed correct for this theme,
 * the fallback should rarely if ever fire in practice.
 *
 * IMPORTANT — WHAT THIS SCRIPT DOES vs WHAT SHOPIFY DOES:
 * This script is a UX layer only. The actual entitlement — the ticket
 * product being priced at ₱0 — MUST be enforced by native Shopify
 * "Buy X get Y" automatic discounts (see setup notes below). Do not price
 * the ticket products at ₱0 directly; if you do, anyone who finds the
 * variant ID can call /cart/add.js directly and get a free ticket without
 * buying anything. This script cannot inject a line item at checkout on
 * its own — Shopify has no native mechanism for that.
 *
 * WHY THREE SEPARATE DISCOUNTS DON'T ACCIDENTALLY STACK:
 * Shopify's Buy X Get Y discounts do not combine with other Buy X Get Y
 * discounts by default. When more than one qualifies on the same order,
 * only one applies — but the exact tiebreaker mechanism (priority, creation
 * order, etc.) was NOT confirmed as a visible admin control in this store's
 * version of Shopify as of 2026-09-09. claimTier()'s discount verification
 * (below) is what actually protects against a wrong/no discount sticking,
 * regardless of how Shopify resolves the tie internally — treat that as
 * the real safety net, not any assumed "priority" field. This matters even
 * more now that claiming is automatic: there is no manual click acting as
 * a final human checkpoint before value is granted.
 *
 * REQUIRED SETUP IN SHOPIFY ADMIN:
 * 1. Three ticket products at REAL prices (Box Premium ₱6,000, VIP ₱8,000,
 *    SVIP ₱12,000), Unlisted status, published to Online Store.
 * 2. Product type = "Gift Certificate" on the ₱500 GC product — confirmed
 *    2026-09-09 this was previously wrong (was set to "Event").
 * 3. Manually curated "Valid Regular Items - Local" collection excluding
 *    Bench Time, Fibre First, Human & Kashieca EDT, and sale items.
 * 4. Five Buy X get Y automatic discounts.
 * 5. Real variant IDs and market handles filled in below — confirm exact
 *    casing via console (window.KSH_MARKET), don't assume.
 *
 * 2026-09-11 REVERT NOTE: the two TEMP/test-only changes that were still
 * present in the previous draft have been reverted for launch:
 *   1. Removed the `svip` entry from CONFIG.tiers.local — SVIP is
 *      International-only per campaign rules.
 *   2. getLocalSubtotal() now excludes Gift Certificate line items again,
 *      so Local customers can only qualify via regular product purchases.
 * Both were explicitly flagged in code comments as "must revert before
 * launch," and today's date falls inside the live VIP/Box Premium window,
 * so this revert was time-sensitive.
 */

const CONFIG = {
  marketHandles: {
    international: "international-excluding-european-countries", // confirmed 2026-09-09
    local: "ph", // confirmed via console 2026-09-03
  },

  // Cosmetic-only date windows — control when the claim status and the
  // product-page violator are shown. NOT a security boundary: claimTier()
  // verifies the real discount behavior at claim-time regardless of what's
  // set here. If these drift out of sync with the actual admin schedule,
  // worst case is a status showing a bit early (auto-claim attempt then
  // correctly self-reverts with "not live yet", nothing charged) or staying
  // hidden a bit after the real discount goes live (missed-conversion
  // annoyance, not a money-safety issue).
  tierWindows: {
    svip: { start: "2026-09-18T00:00:00+08:00", end: "2026-09-30T23:59:59+08:00" },
    vip: { start: "2026-09-13T00:00:00+08:00", end: "2026-09-17T23:59:59+08:00" },
    boxPremium: { start: "2026-09-13T00:00:00+08:00", end: "2026-09-17T23:59:59+08:00" },
  },

  vendorExclusions: ["Bench Time", "Fibre First", "Human & Kashieca EDT"],

  // *** MUST CONFIRM — REPLACE WITH THE REAL COLLECTION HANDLE ***
  // getLocalSubtotal() only counts items that are actual members of this
  // Shopify collection, fetched live via the public collection JSON
  // endpoint. Find the real handle in Shopify admin > Products >
  // Collections > open "Valid Regular Items - Local" > the handle is the
  // URL-safe slug shown in its preview link (e.g. "valid-regular-items-local"
  // — NOT confirmed as this store's actual handle, do not assume this is
  // correct). This collection must also be published to the Online Store
  // sales channel, even if not linked in navigation, or the endpoint 404s
  // and every item will be treated as ineligible (fails closed, not open).
  localEligibleCollectionHandle: "kim-soo-hyun-our-forever-with-you",

  // *** CONFIRM THIS AGAINST THE REAL SECTION FILENAME BEFORE TRUSTING IT ***
  // Find it in Shopify admin > Online Store > Edit code > sections/ — the
  // filename (minus .liquid) of whichever section's markup contains your
  // cart drawer classes (cart__item, cart__discount, popout-select, etc).
  // If the markup actually lives in a snippet rendered inside a larger
  // section instead, this needs to be that containing section's id, not
  // the snippet's name — check for that case specifically.
  cartDrawerSectionId: "cart-drawer",

  // Product page promo box (the "violator") — shown only on the GC
  // product page, only for tiers currently inside their cosmetic window.
  gcProductHandle: "bench-gift-certificate",

  // *** TEMP TESTING ONLY — REMOVE BEFORE LAUNCH ***
  // Lets testing happen on a separate test GC product without touching
  // the real "bench-gift-certificate" product or its live data. Treated
  // as equivalent to a real Gift Certificate for BOTH the violator box
  // display AND the International subtotal calculation.
  testGCProductHandles: ["bench-gift-certificate-test"],

  tiers: {
    international: [
      { key: "svip", label: "SVIP", threshold: 12000, variantId: 44340309622918 },
      { key: "vip", label: "VIP", threshold: 8000, variantId: 44340322959494 },
      { key: "boxPremium", label: "Box Premium", threshold: 6000, variantId: 44340327776390 },
    ],
    local: [
      // SVIP intentionally NOT included — International-only per campaign rules.
      { key: "svip", label: "SVIP", threshold: 12000, variantId: 44340309622918 },
      { key: "vip", label: "VIP", threshold: 8000, variantId: 44340322959494 },
      { key: "boxPremium", label: "Box Premium", threshold: 6000, variantId: 44340327776390 },
    ],
  },
};

window.KSH_CONFIG = CONFIG; // for debugging — check window.KSH_CONFIG in console

// Prevents any two syncEligibility() runs — whether from the 2s interval
// or the post-cart-add trigger — from executing concurrently. This is the
// direct fix for the duplicate-line/reload-loop bug that caused the
// previous auto-claim attempt to be reverted: that bug came from
// overlapping eligibility checks, not from automation itself.
let syncInProgress = false;

// Tracks tiers that just failed their real-discount verification (e.g.
// cosmetic window says "live" but the actual Shopify discount didn't fire —
// commonly because the cart's subtotal calculation is looser than the
// discount's real eligibility rules, such as collection membership or
// sale-item exclusions that this script does not currently check). Without
// this, syncEligibility's 2-second poll would retry the exact same doomed
// claim forever: add ticket -> discount fails -> revert -> 2s later, cart
// hasn't changed, so it tries again -> fails again -> repeat indefinitely.
// That's the "always prompting" symptom — this cooldown stops the loop
// without needing to know WHY the discount didn't fire.
const failedClaimCooldowns = {};
const FAILED_CLAIM_COOLDOWN_MS = 30000;

function isOnFailedCooldown(tierKey) {
  const until = failedClaimCooldowns[tierKey];
  return typeof until === "number" && Date.now() < until;
}

function setFailedCooldown(tierKey) {
  failedClaimCooldowns[tierKey] = Date.now() + FAILED_CLAIM_COOLDOWN_MS;
}

function clearFailedCooldown(tierKey) {
  delete failedClaimCooldowns[tierKey];
}

async function dedupeTicketLines(cart) {
  let changed = false;
  let lastBody = null;
  for (const t of getAllTiers()) {
    const matchingLines = cart.items.filter((i) => i.variant_id === t.variantId);
    if (matchingLines.length === 0) continue;

    for (let idx = 0; idx < matchingLines.length; idx++) {
      const line = matchingLines[idx];
      if (idx === 0) {
        if (line.quantity !== 1) {
          const res = await fetch("/cart/change.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id: line.key, quantity: 1, sections: [CONFIG.cartDrawerSectionId] }),
          });
          lastBody = await res.json().catch(() => null);
          changed = true;
        }
      } else {
        // Duplicate line for the same ticket variant — most likely from
        // someone reaching the Unlisted product's direct URL and adding it
        // through the normal theme form. Remove entirely.
        const res = await fetch("/cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: line.key, quantity: 0, sections: [CONFIG.cartDrawerSectionId] }),
        });
        lastBody = await res.json().catch(() => null);
        changed = true;
      }
    }
  }

  if (!changed) return { changed: false, drawerUpdated: true };

  const drawerUpdated = lastBody ? updateCartDrawerDOM(lastBody.sections) : false;
  return { changed: true, drawerUpdated };
}

function isTierWindowOpen(tierKey) {
  const window = CONFIG.tierWindows[tierKey];
  if (!window) return true;
  const now = new Date();
  return now >= new Date(window.start) && now <= new Date(window.end);
}

function getAllTiers() {
  return [...CONFIG.tiers.international, ...CONFIG.tiers.local];
}

function renderGCProductViolator() {
  // TEMP: also matches CONFIG.testGCProductHandles for testing — remove
  // that array (and this .some() check) before launch.
  const isGCPage =
    window.location.pathname.includes(`/products/${CONFIG.gcProductHandle}`) ||
    CONFIG.testGCProductHandles.some((h) => window.location.pathname.includes(`/products/${h}`));
  if (!isGCPage) return;

  const existing = document.getElementById("ksh-gc-violator");
  if (existing) existing.remove();

  const openTiers = CONFIG.tiers.international.filter((t) => isTierWindowOpen(t.key));
  if (openTiers.length === 0) return;

  const box = document.createElement("div");
  box.id = "ksh-gc-violator";
  box.style.cssText = `
    border: 2px solid #f0c14b;
    background: #fffbea;
    border-radius: 8px;
    padding: 14px 16px;
    margin: 16px 0;
    font-size: 14px;
    line-height: 1.6;
  `;
  box.innerHTML = openTiers
    .map(
      (t) =>
        `<div>🎁 Spend ₱${t.threshold.toLocaleString()} and you'll get a FREE ${t.label} Ticket for Kim Soo Hyun Fan Meet</div>`
    )
    .join("");

  const heading = document.querySelector("h1");
  if (heading) heading.after(box);
}

function getCurrentMarket() {
  // TEMP TESTING OVERRIDE — lets you force a specific market path without
  // switching Shopify's real country/market selector or using a VPN. Add
  // ?ksh_test_market=international or ?ksh_test_market=local to the page
  // URL to override detection for that page load only.
  const params = new URLSearchParams(window.location.search);
  const override = params.get("ksh_test_market");
  if (override === "international" || override === "local") {
    console.warn(`[KSH] TEST OVERRIDE ACTIVE — forcing market to "${override}" via URL param.`);
    return override;
  }

  const handle = window.KSH_MARKET;
  if (handle === CONFIG.marketHandles.international) return "international";
  if (handle === CONFIG.marketHandles.local) return "local";
  return null; // unknown/unset — do nothing rather than guess wrong
}

async function getCart() {
  const res = await fetch("/cart.js");
  return res.json();
}

// Fetches the FULL "Valid Regular Items - Local" collection once (all
// pages, up to Shopify's 250-per-page limit), caches it for the page's
// lifetime, and returns both the set of eligible product IDs and each
// variant's real sale status (compare_at_price > price) pulled straight
// from that same response — no separate per-item fetch needed, since the
// collection endpoint already includes variant pricing data.
//
// FAILS CLOSED: if the collection handle is wrong, or the collection isn't
// published to the Online Store sales channel, this endpoint 404s and the
// eligible set stays empty — meaning every item is treated as ineligible
// rather than silently letting everything through. That's a deliberate
// safety choice, but it also means a wrong handle looks like "nothing
// qualifies" rather than an obvious error — check the console warning.
let eligibleCollectionLoadPromise = null;
let eligibleProductIds = null;
const saleStatusByVariantId = {};

async function loadEligibleCollectionData() {
  if (eligibleProductIds) return eligibleProductIds;
  if (eligibleCollectionLoadPromise) return eligibleCollectionLoadPromise;

  eligibleCollectionLoadPromise = (async () => {
    const ids = new Set();
    let page = 1;

    while (true) {
      let res;
      try {
        res = await fetch(
          `/collections/${CONFIG.localEligibleCollectionHandle}/products.json?limit=250&page=${page}`
        );
      } catch (err) {
        console.warn("[KSH] Network error fetching eligible collection — treating as empty/ineligible.", err);
        break;
      }

      if (!res.ok) {
        console.warn(
          `[KSH] Eligible collection fetch failed (status ${res.status}) for handle ` +
            `"${CONFIG.localEligibleCollectionHandle}" — check the handle is correct AND the ` +
            `collection is published to the Online Store sales channel. Treating as empty/ineligible ` +
            `so no one qualifies incorrectly, but this means the promo won't work for Local until fixed.`
        );
        break;
      }

      const data = await res.json().catch(() => null);
      const products = data?.products || [];
      if (products.length === 0) break;

      for (const p of products) {
        ids.add(p.id);
        for (const v of p.variants || []) {
          const compareAt = Number(v.compare_at_price || 0);
          const price = Number(v.price || 0);
          saleStatusByVariantId[v.id] = compareAt > 0 && compareAt > price;
        }
      }

      if (products.length < 250) break;
      page++;
    }

    eligibleProductIds = ids;
    return ids;
  })();

  return eligibleCollectionLoadPromise;
}

// Checks BOTH conditions your setup notes call for: the item must be an
// actual member of the curated collection, AND must not currently be on
// sale. Returns false — treats as ineligible — if collection data never
// loaded (wrong handle, unpublished collection, network failure).
async function isItemEligible(item) {
  const eligibleIds = await loadEligibleCollectionData();
  if (!eligibleIds.has(item.product_id)) return false;
  if (saleStatusByVariantId[item.variant_id]) return false;
  return true;
}

// Updates the live cart drawer's HTML in place using Shopify's Sections
// API response, instead of reloading the page. This is what lets the
// customer actually SEE the free ticket land without any navigation.
// If the section id is wrong/unconfirmed or the section HTML isn't found,
// this deliberately does nothing and returns false — callers fall back to
// attemptSoftRefresh(), NOT a reload.
function updateCartDrawerDOM(sections) {
  if (!sections || !sections[CONFIG.cartDrawerSectionId]) return false;

  const container = getCartContainer();
  if (!container) return false;

  const html = sections[CONFIG.cartDrawerSectionId];
  const parsed = new DOMParser().parseFromString(html, "text/html");
  // The rendered section usually wraps its own drawer element — look for
  // the same kind of node getCartContainer() already matches.
  const newContent =
    parsed.querySelector("[data-cart-drawer], .cart-drawer, cart-drawer") || parsed.body.firstElementChild;
  if (!newContent) return false;

  container.replaceWith(newContent);

  // AOS (Animate On Scroll) fix: elements with [data-aos] start hidden
  // (opacity: .001) until AOS adds an "aos-animate" class to reveal them.
  // AOS only watches elements present when the page loads/scrolls — a
  // freshly injected node from the Sections API was never being watched,
  // so it never got that class and stayed invisible even though it was
  // correctly in the DOM with the correct content. Applying the class
  // manually here reproduces what AOS would have done naturally.
  if (newContent.hasAttribute && newContent.hasAttribute("data-aos")) {
    newContent.classList.add("aos-animate");
  }
  newContent.querySelectorAll?.("[data-aos]").forEach((el) => {
    el.classList.add("aos-animate");
  });
  if (window.AOS && typeof window.AOS.refreshHard === "function") {
    window.AOS.refreshHard();
  } else if (window.AOS && typeof window.AOS.refresh === "function") {
    window.AOS.refresh();
  }

  return true;
}

// Best-effort nudge to make sure the customer notices the free ticket
// rather than needing to open the drawer themselves.
//
// CONFIRMED 2026-09-11: manually adding "js-drawer-open-cart" correctly
// triggers the CSS visibility/animation, but the drawer content stayed
// EMPTY — because this theme's real click handler (bound to
// [data-cart-drawer-toggle]) does more than toggle a class: it's also
// what clones the drawer's <template data-cart-drawer-template> content
// into a live, visible area. Setting the class directly bypasses that
// handler entirely, so the CSS opens an empty shell. Triggering a real
// click on the toggle element runs the theme's actual logic — class add
// AND template cloning together — instead of us trying to replicate
// pieces of it ourselves.
function tryOpenCartDrawer() {
  try {
    const toggle = document.querySelector("[data-cart-drawer-toggle]");
    if (toggle) {
      toggle.click();
      return;
    }

    // Fallback only if no toggle element exists at all — won't populate
    // content, but at least won't leave things worse than doing nothing.
    document.documentElement.classList.add("js-drawer-open-cart");
    document.body.classList.add("js-drawer-open-cart");
  } catch (err) {
    console.warn("[KSH] Could not auto-open cart drawer — theme-specific, not critical.", err);
  }
}

async function addTicket(variantId) {
  const res = await fetch("/cart/add.js", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      items: [{ id: variantId, quantity: 1 }],
      sections: [CONFIG.cartDrawerSectionId],
    }),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    showSoldOutMessage(body?.description || "This ticket tier is no longer available.");
    return { success: false };
  }

  const body = await res.json().catch(() => null);
  const drawerUpdated = body ? updateCartDrawerDOM(body.sections) : false;
  return { success: true, drawerUpdated };
}

async function removeTicketLines(cart, variantId) {
  const linesToRemove = cart.items.filter((item) => item.variant_id === variantId);
  let lastBody = null;
  for (const line of linesToRemove) {
    const res = await fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: line.key, quantity: 0, sections: [CONFIG.cartDrawerSectionId] }),
    });
    lastBody = await res.json().catch(() => null);
  }
  const drawerUpdated = lastBody ? updateCartDrawerDOM(lastBody.sections) : false;
  return { drawerUpdated };
}

function isAnyTicketVariant(variantId) {
  return getAllTiers().some((t) => t.variantId === variantId);
}

function getInternationalSubtotal(cart) {
  // TEMP: also counts CONFIG.testGCProductHandles as if they were real
  // Gift Certificates, regardless of their actual product_type — lets
  // testing happen without needing to configure that field on a test
  // product. Remove the .includes() check before launch.
  return (
    cart.items
      .filter(
        (item) =>
          item.product_type === "Gift Certificate" || CONFIG.testGCProductHandles.includes(item.handle)
      )
      .reduce((sum, item) => sum + item.line_price, 0) / 100
  );
}

async function getLocalSubtotal(cart) {
  // Local customers qualify by REGULAR PRODUCT subtotal only. Gift
  // Certificate purchases are excluded here per actual campaign mechanics —
  // Local customers are not meant to be able to buy a GC and receive a free
  // ticket.
  //
  // Vendor exclusion alone was never sufficient — it doesn't check real
  // collection membership or sale status. isItemEligible() checks actual
  // membership in the curated Local collection plus current sale status,
  // both pulled from a real fetch of that collection's data (cached once
  // for the page's lifetime, not refetched per item or per poll).
  //
  // TEMP TESTING ONLY — REMOVE BEFORE LAUNCH: items matching
  // CONFIG.testGCProductHandles bypass the Gift Certificate exclusion,
  // the vendor exclusion, AND the collection-membership check entirely —
  // always counted as eligible here, regardless of the real business rule
  // that Local customers shouldn't qualify via GC purchases. This exists
  // ONLY so the same physical test product can be used to test both
  // market paths without needing a second test product or touching real
  // collection/vendor data. This is exactly the kind of thing that must
  // be removed before launch — a real GC purchase must NOT count toward
  // the Local subtotal in production, per campaign rules.
  const candidateItems = cart.items.filter(
    (item) =>
      !isAnyTicketVariant(item.variant_id) &&
      (CONFIG.testGCProductHandles.includes(item.handle) ||
        (item.product_type !== "Gift Certificate" && !CONFIG.vendorExclusions.includes(item.vendor)))
  );

  const eligibilityChecks = await Promise.all(
    candidateItems.map((item) =>
      CONFIG.testGCProductHandles.includes(item.handle) ? Promise.resolve(true) : isItemEligible(item)
    )
  );

  return (
    candidateItems
      .filter((_, idx) => eligibilityChecks[idx])
      .reduce((sum, item) => sum + item.line_price, 0) / 100
  );
}

function getCartContainer() {
  return document.querySelector("[data-cart-drawer], .cart-drawer, cart-drawer");
}

// Removes BOTH banner types — the yellow "claiming" banner AND any
// lingering "sold out / not live" message. Previously removeClaimBanner()
// only cleared the claim banner, and showSoldOutMessage() gave its element
// a className instead of an id, so it could never find and remove a PRIOR
// sold-out message before creating a new one. That let banners stack up
// visually across repeated fast attempts (e.g. before the cooldown fix
// took effect, or from any other rapid back-to-back sync passes) instead
// of cleanly replacing each other. Every show/remove call now goes through
// this single function so at most one KSH banner is ever visible.
function removeAllKshBanners() {
  const claimBanner = document.getElementById("ksh-claim-banner");
  if (claimBanner) claimBanner.remove();
  const soldOutBanner = document.getElementById("ksh-sold-out-banner");
  if (soldOutBanner) soldOutBanner.remove();
}

function removeClaimBanner() {
  removeAllKshBanners();
}

function showClaimingStatus(tier) {
  removeAllKshBanners();
  const el = document.createElement("div");
  el.id = "ksh-claim-banner";
  el.setAttribute("role", "status");
  el.style.cssText = `
    background: linear-gradient(135deg, #fff4d6, #ffe9b3);
    border: 1px solid #f0c14b;
    border-radius: 10px;
    padding: 14px 16px;
    margin: 12px 0;
    text-align: center;
    font-family: inherit;
  `;
  el.innerHTML = `
    <div style="font-size: 15px; font-weight: 700; color: #7a4a00; line-height: 1.4;">
      🎉 You're entitled to a FREE ${tier.label} KSH Fan Meet ticket — adding it now...
    </div>
  `;

  const container = getCartContainer();
  if (container) container.prepend(el);
}

function showSoldOutMessage(message) {
  removeAllKshBanners();
  const el = document.createElement("div");
  el.id = "ksh-sold-out-banner";
  el.setAttribute("role", "status");
  el.textContent = `Sorry — ${message}`;
  const container = getCartContainer();
  if (container) container.prepend(el);
  setTimeout(() => {
    // Only remove if it's still THIS instance — a newer banner may have
    // already replaced it, and we don't want this stale timeout to delete
    // a fresh, unrelated banner that happens to share the same id.
    if (document.getElementById("ksh-sold-out-banner") === el) el.remove();
  }, 6000);
}

// FALLBACK: hard reload. Used ONLY when updateCartDrawerDOM() fails
// (wrong/unconfirmed section id, drawer not section-rendered in this
// theme) — the primary path (Sections API swap) never reaches this. A
// cooldown prevents rapid repeat reloads if cart state is flapping
// between two tiers in quick succession.
function reloadWithCooldown() {
  const RELOAD_COOLDOWN_MS = 8000;
  const lastReload = Number(sessionStorage.getItem("kshLastReload") || 0);
  if (Date.now() - lastReload > RELOAD_COOLDOWN_MS) {
    sessionStorage.setItem("kshLastReload", String(Date.now()));
    // Flag so the fresh page load knows to reopen the drawer for the
    // customer, instead of leaving them staring at a closed cart after
    // a reload they didn't initiate themselves.
    sessionStorage.setItem("kshOpenCartAfterReload", "1");
    window.location.reload();
  } else {
    console.warn(
      "[KSH] Skipped a reload because one already happened recently — " +
        "if this keeps happening, the cart state is likely flapping between " +
        "two tiers. Check CONFIG and current cart contents."
    );
  }
}

async function claimTier(tier) {
  const cart = await getCart();

  // Covers the upgrade case: if any other tier's ticket(s) are already in
  // the cart, remove all of them first so the customer doesn't end up with
  // more than one. Uses .filter(), not .find() — a single match would miss
  // the case where more than one wrong ticket is present at once.
  const otherTickets = getAllTiers().filter(
    (t) => t.key !== tier.key && cart.items.some((i) => i.variant_id === t.variantId)
  );
  for (const t of otherTickets) {
    await removeTicketLines(cart, t.variantId);
  }

  const result = await addTicket(tier.variantId);
  if (!result.success) {
    return { claimed: false }; // addTicket already surfaced the sold-out message
  }

  // Verify the native Buy X Get Y discount actually fired, rather than
  // trusting a hardcoded schedule. Compare against original_line_price
  // (the pre-discount total) and total_discount — NOT final_line_price vs
  // line_price, which are effectively always equal to each other in this
  // store's cart response and told us nothing. This check matters even
  // more now that claiming is automatic — it's the only thing standing
  // between "cart looks eligible" and a ticket actually costing ₱0.
  const updatedCart = await getCart();
  const addedLine = updatedCart.items.find((i) => i.variant_id === tier.variantId);
  const discountApplied =
    addedLine && (addedLine.total_discount > 0 || addedLine.line_price < addedLine.original_line_price);

  if (!discountApplied) {
    await removeTicketLines(updatedCart, tier.variantId);
    showSoldOutMessage(
      "this offer isn't live yet. Please check back once the promo period starts."
    );
    setFailedCooldown(tier.key);
    return { claimed: false };
  }

  clearFailedCooldown(tier.key);
  document.dispatchEvent(new CustomEvent("cart:updated", { detail: { cart: updatedCart } }));

  // No refresh/reload triggered here anymore — the caller (syncEligibility)
  // consolidates this result with any prior removal from the same pass, so
  // only ONE refresh happens per tier-change event, not two. Two separate
  // refresh attempts in immediate succession was the actual bug: removing
  // a stale ticket triggered its own reload, which consumed the reload
  // cooldown — silently blocking the second reload this function used to
  // trigger for the newly claimed ticket. The new ticket was correctly
  // added and discounted, it just never got shown.
  return { claimed: true, drawerUpdated: result.drawerUpdated };
}

async function syncEligibility() {
  // Hard stop against overlapping runs — this is what makes auto-claim
  // safe. Without this, the 2s interval and the post-cart-add trigger
  // could both land while a previous claimTier() is still awaiting its
  // fetch calls, which is exactly what caused duplicate lines before.
  if (syncInProgress) return;
  syncInProgress = true;

  try {
    const market = getCurrentMarket();
    if (!market) {
      removeClaimBanner();
      return;
    }

    let cart = await getCart();
    let mutated = false;
    let drawerUpdated = false;

    const dedupeResult = await dedupeTicketLines(cart);
    if (dedupeResult.changed) {
      mutated = true;
      drawerUpdated = dedupeResult.drawerUpdated;
      cart = await getCart(); // refresh local view after dedupe mutation
    }

    const subtotal =
      market === "international" ? getInternationalSubtotal(cart) : await getLocalSubtotal(cart);
    const tierList = CONFIG.tiers[market];
    const qualifyingTier = tierList.find((t) => subtotal >= t.threshold) || null;

    // IMPORTANT: check ALL held ticket tiers, not just the first match.
    // Using .find() here previously meant that if two different tiers'
    // tickets ended up in the cart at once (e.g. a customer reaching two
    // different Unlisted ticket product URLs directly, or a stray manual
    // add), the check would find whichever tier came first, see it matched
    // the qualifying tier, and stop — completely missing a second,
    // incorrect free ticket sitting right next to it. That's a real
    // money-safety gap, not just a testing artifact: a customer could end
    // up with two free tickets for one qualifying purchase.
    const heldTierTickets = tierList.filter((t) =>
      cart.items.some((i) => i.variant_id === t.variantId)
    );
    const wrongTickets = heldTierTickets.filter(
      (t) => !qualifyingTier || t.key !== qualifyingTier.key
    );

    // Remove every held ticket that isn't the correct entitlement — covers
    // the single stale-tier case exactly as before, AND the case where
    // more than one wrong ticket is present at once. Does NOT return/reload
    // immediately anymore — the fix below is to fall through and let a
    // qualifying claim happen in this SAME pass, so a tier upgrade (e.g.
    // Box Premium -> VIP) removes the old ticket and adds the new one
    // before any single consolidated refresh happens at the end.
    if (wrongTickets.length > 0) {
      for (const t of wrongTickets) {
        const result = await removeTicketLines(cart, t.variantId);
        drawerUpdated = result.drawerUpdated;
      }
      mutated = true;
      removeClaimBanner();
      cart = await getCart(); // refresh local view after removal
    }

    if (!qualifyingTier) {
      removeClaimBanner();
    } else {
      const alreadyHasCorrectTicket = cart.items.some((i) => i.variant_id === qualifyingTier.variantId);

      if (!alreadyHasCorrectTicket && isTierWindowOpen(qualifyingTier.key) && !isOnFailedCooldown(qualifyingTier.key)) {
        // AUTO-CLAIM: no click required. showClaimingStatus is purely
        // informational — claimTier() below is what actually adds the
        // item and verifies the real discount fired before letting it
        // stick. Its result feeds into this pass's single consolidated
        // refresh below, rather than triggering its own separate one.
        showClaimingStatus(qualifyingTier);
        const claimResult = await claimTier(qualifyingTier);
        if (claimResult.claimed) {
          mutated = true;
          drawerUpdated = claimResult.drawerUpdated;
        }
      } else if (alreadyHasCorrectTicket) {
        removeClaimBanner();
      }
    }

    // Exactly ONE refresh decision for this entire pass, covering any
    // combination of removal + claim that happened above.
    if (mutated) {
      if (drawerUpdated) {
        tryOpenCartDrawer();
      } else {
        console.warn(
          "[KSH] Cart changed but drawer DOM update failed — falling back to full reload. " +
            "Check CONFIG.cartDrawerSectionId against your theme's actual section id."
        );
        reloadWithCooldown();
      }
    }
  } finally {
    syncInProgress = false;
  }
}

// --- INTEGRATION ---
// 1. Market detection: add this small snippet in the SAME Custom Liquid
//    block, BEFORE the script tag below:
//      <script>
//        window.KSH_MARKET = "{{ localization.market.handle }}";
//      </script>
//
// 2. Eligibility checking: polls in the background and now claims
//    automatically as soon as a customer qualifies. The syncInProgress
//    lock above ensures this never overlaps with itself, whether
//    triggered by the interval below or by a cart-add submit.
document.addEventListener("submit", (e) => {
  if (e.target?.action?.includes("/cart/add")) {
    setTimeout(syncEligibility, 800);
  }
});

setInterval(syncEligibility, 2000);
window.addEventListener("DOMContentLoaded", () => {
  syncEligibility();
  renderGCProductViolator();

  // If this page load happened because of a KSH-triggered reload, reopen
  // the drawer so the customer sees their ticket without needing to click
  // the cart icon themselves.
  //
  // Retrying at multiple delays, plus a hash-based trigger, since a single
  // attempt at a fixed delay proved unreliable for some tier-change
  // scenarios — likely a timing race with the theme's own drawer init on
  // a fresh page load. All of these are harmless to repeat if the drawer
  // is already correctly open by the time a later one runs.
  if (sessionStorage.getItem("kshOpenCartAfterReload") === "1") {
    sessionStorage.removeItem("kshOpenCartAfterReload");

    const attemptOpen = () => {
      tryOpenCartDrawer();

      // Second, independent trigger: this theme's close link uses
      // href="#cart-drawer", suggesting a hash-based open mechanism may
      // also exist globally. Clearing then resetting the hash forces a
      // hashchange event even if it's already set to this value.
      try {
        if (window.location.hash === "#cart-drawer") {
          window.location.hash = "";
        }
        window.location.hash = "cart-drawer";
      } catch (err) {
        console.warn("[KSH] Hash-based drawer open attempt failed — not critical.", err);
      }
    };

    setTimeout(attemptOpen, 300);
    setTimeout(attemptOpen, 800);
    setTimeout(attemptOpen, 1500);
  }
});